export interface StoreAddress {
  jibunAddress?: string;
  roadAddress?: string;
  detailAddress?: string;
}

export type BusinessHourType = 'DAILY' | 'WEEKDAY_WEEKEND' | 'DAY_OF_WEEK';
export type BreakTimeType = 'DAILY' | 'WEEKDAY_WEEKEND' | 'DAY_OF_WEEK';
export type HolidayType = 'MONTHLY_WEEK_AND_DAY' | 'SPECIFIC_DATE' | 'WEEKLY_DAY' | 'MONTHLY_DATE'; // WEEKLY_DAY, MONTHLY_DATE: 크롤링 데이터에서 사용
export type StoreOrderType = 'DELIVERY' | 'TAKEOUT' | 'BOTH';

export type CorporateType = keyof typeof CORPORATE_TYPES;
export const DELIVERY_SCOPE_TYPES = {
  RADIUS: 'RADIUS',
  REGION: 'REGION',
  NONE: 'NONE',
} as const;

// Value 를 타입으로 추출
export type DeliveryScopeType = (typeof DELIVERY_SCOPE_TYPES)[keyof typeof DELIVERY_SCOPE_TYPES];
export const DELIVERY_SCOPE_TYPE_LABELS: Record<DeliveryScopeType, string> = {
  [DELIVERY_SCOPE_TYPES.RADIUS]: '반경',
  [DELIVERY_SCOPE_TYPES.REGION]: '지역',
  [DELIVERY_SCOPE_TYPES.NONE]: '',
} as const;

export const CORPORATE_TYPES = {
  ADMIN: '꼬르륵 본사',
  BONSA: '본사',
  BONBU: '본부',
  CENTER: '센터',
  JISA: '지사',
  JIJUM: '지점',
} as const;

export interface BusinessHour {
  businessHourType: BusinessHourType;
  mondayStartTime?: string;
  mondayEndTime?: string;
  mondayAllDay: boolean;
  tuesdayStartTime?: string;
  tuesdayEndTime?: string;
  tuesdayAllDay: boolean;
  wednesdayStartTime?: string;
  wednesdayEndTime?: string;
  wednesdayAllDay: boolean;
  thursdayStartTime?: string;
  thursdayEndTime?: string;
  thursdayAllDay: boolean;
  fridayStartTime?: string;
  fridayEndTime?: string;
  fridayAllDay: boolean;
  saturdayStartTime?: string;
  saturdayEndTime?: string;
  saturdayAllDay: boolean;
  sundayStartTime?: string;
  sundayEndTime?: string;
  sundayAllDay: boolean;
}

export interface BreakTime {
  breakTimeType: BreakTimeType;
  mondayStartTime?: string[];
  mondayEndTime?: string[];
  tuesdayStartTime?: string[];
  tuesdayEndTime?: string[];
  wednesdayStartTime?: string[];
  wednesdayEndTime?: string[];
  thursdayStartTime?: string[];
  thursdayEndTime?: string[];
  fridayStartTime?: string[];
  fridayEndTime?: string[];
  saturdayStartTime?: string[];
  saturdayEndTime?: string[];
  sundayStartTime?: string[];
  sundayEndTime?: string[];
}

type SpecificDate = [number, number, number];

export interface Holiday {
  holidayType: HolidayType;
  daysOfWeek: dayKey[];
  daysOfMonth: number[];
  weeksOfMonth: WeekKey[];
  specificDates: SpecificDate[];
}

export interface DeliveryArea {
  addressRegion1Name: string;
  addressRegion2Name: string;
  addressRegion3Name: string;
  addressRegion4Name?: string;
}

export interface StoreDeliveryAreas {
  // baseDeliveryFee: number;
  deliveryAreas: DeliveryArea[];
}

export interface DeliveryCheck {
  freeDeliveryMode: boolean;
}

export interface OrderAmountDeliveryFeeRule {
  orderAmountDeliveryFeeRuleId: number;
  orderAmountLowerLimit: number;
  extraDeliveryFee: number;
}

export interface StoreOrderAmountDeliveryFeeRule {
  orderAmountDeliveryFeeRules: OrderAmountDeliveryFeeRule[];
}

export interface StoreDeliveryAreaFeeRule {
  deliveryFeeRules: DeliveryAreaFeeRule[];
}

export interface StoreDistanceDeliveryFeeRule {
  deliveryFeeRules: DistanceDeliveryFeeRule[];
}

export interface DeliveryAreaFeeRule {
  deliveryAreaFeeRuleId: number;
  extraDeliveryFee: number;
  deliveryAreas: DeliveryArea[];
}

export interface DistanceDeliveryFeeRule {
  distanceDeliveryFeeRuleId: number;
  deliveryDistanceLowerLimit: number;
  extraFeeDistanceUnit: number;
  extraDeliveryFee: number;
}

export interface StoreModel {
  storeId: number;
  storeName: string;
  storeNumber?: string;
  storeAddress: StoreAddress;
  mainCategory: string[];
  businessRegistrationCertificateStoreName: string;
  ownerName?: string;
  businessRegistrationCertificateNumber: string;
  bankType?: string;
  bankNumber?: string;
  bankOwnerName?: string;
  alcohol: boolean;
  corporateName: string;
  corporateType: CorporateType;
  corporation: boolean;
  storeStatusType: string; // TODO 타입 만들기
  // storeOwnerComment?: string; // 가게소개
  // storeNotice: string; // 사장님 공지
  storeIngredients?: string; // 원산지
  shopInShop: boolean; // 샵인샵 여부

  // files
  businessDeclarationCertificateUrl?: string;
  businessRegistrationCertificateUrl?: string;
  bankFileUrl?: string;

  businessHourList?: BusinessHour;
  storeBreakTimeList: BreakTime[];
  storeHolidayList: Holiday[];
  storeOrderType: { storeOrderType: StoreOrderType };
  storeDeliveryAreas: StoreDeliveryAreas;
  deliveryCheck: DeliveryCheck;
  storeOrderAmountDeliveryFeeRule: StoreOrderAmountDeliveryFeeRule;
  storeDeliveryAreaFeeRule: StoreDeliveryAreaFeeRule;
  storeDistanceDeliveryFeeRule: StoreDistanceDeliveryFeeRule;

  // 반경
  //  배달 설정
  deliveryScopeType: DeliveryScopeType | undefined | null;
  //  배달 반경 km
  radiusKm: number | undefined | null;
}

type AddressRegion4Group = {
  addressRegion1Name: string;
  addressRegion2Name: string;
  addressRegion3Name: string;
  addressRegion4Name: string[];
};

type AddressRegion3Group = {
  addressRegion1Name: string;
  addressRegion2Name: string;
  addressRegion3Name: string[];
};

type GroupedDeliveryArea = AddressRegion3Group | AddressRegion4Group;

export const groupDeliveryAreas = (areas: DeliveryArea[]): GroupedDeliveryArea[] => {
  const result: GroupedDeliveryArea[] = [];

  for (const area of areas) {
    if (area.addressRegion4Name) {
      const index = result.findIndex(
        (v) =>
          v.addressRegion1Name === area.addressRegion1Name &&
          v.addressRegion2Name === area.addressRegion2Name &&
          v.addressRegion3Name === area.addressRegion3Name
      );

      if (index === -1) {
        result.push({
          addressRegion1Name: area.addressRegion1Name,
          addressRegion2Name: area.addressRegion2Name,
          addressRegion3Name: area.addressRegion3Name,
          addressRegion4Name: [area.addressRegion4Name],
        });
        continue;
      }

      (result[index] as AddressRegion4Group).addressRegion4Name.push(area.addressRegion4Name);

      // 중복 제거
      // ex)
      // {
      //     "id": 506,
      //     "addressRegion1Name": "충청북도",
      //     "addressRegion2Name": "청주시",
      //     "addressRegion3Name": "흥덕구", <= 제거 대상
      //     "addressRegion4Name": "",
      //     "code": 4311300000
      // },
      // {
      //     "id": 507,
      //     "addressRegion1Name": "충청북도",
      //     "addressRegion2Name": "청주시",
      //     "addressRegion3Name": "흥덕구",
      //     "addressRegion4Name": "가경동",
      //     "code": 4311311300
      // },
      for (let i = 0; i < result.length; i++) {
        const v = result[i];

        if (
          v.addressRegion1Name === area.addressRegion1Name &&
          v.addressRegion2Name === area.addressRegion2Name &&
          Array.isArray(v.addressRegion3Name)
        ) {
          if (v.addressRegion3Name[0] == area.addressRegion3Name) {
            result.splice(i, 1);
            break;
          }

          const idx = v.addressRegion3Name.indexOf(area.addressRegion3Name);
          if (idx !== -1) {
            v.addressRegion3Name.splice(idx, 1);
          }

          break;
        }
      }

      continue;
    }

    // ----- 주소3까지 있는 경우 ----- //

    // 주소4 리스트를 갖는 그룹으로 추가된 적 있는 경우 - 얼리리턴
    const hasAddress4 = result.some(
      (v) =>
        v.addressRegion1Name === area.addressRegion1Name &&
        v.addressRegion2Name === area.addressRegion2Name &&
        v.addressRegion3Name === area.addressRegion3Name
    );
    if (hasAddress4) continue;

    const index = result.findIndex(
      (v) =>
        v.addressRegion1Name === area.addressRegion1Name &&
        v.addressRegion2Name === area.addressRegion2Name &&
        Array.isArray(v.addressRegion3Name)
    );

    if (index === -1) {
      result.push({
        addressRegion1Name: area.addressRegion1Name,
        addressRegion2Name: area.addressRegion2Name,
        addressRegion3Name: [area.addressRegion3Name],
      });
      continue;
    }

    (result[index] as AddressRegion3Group).addressRegion3Name.push(area.addressRegion3Name);
  }

  return result;
};

// 주차 매핑
export const WEEKS = {
  1: '첫째주',
  2: '둘째주',
  3: '셋째주',
  4: '넷째주',
  5: '다섯째주',
} as const;

export type WeekKey = keyof typeof WEEKS;
export type dayKey = keyof typeof DAYS;

// 요일 매핑
export const DAYS = {
  1: '월요일',
  2: '화요일',
  3: '수요일',
  4: '목요일',
  5: '금요일',
  6: '토요일',
  7: '일요일',
} as const;

export const validateBusinessHour = (allDay: boolean, startTime?: string, endTime?: string) => {
  if (!allDay && (!startTime || !endTime)) {
    return false;
  }

  return true;
};

export const validateBreakTime = (startTime?: string[], endTime?: string[]) => {
  if (!startTime || !endTime || startTime.length < 2 || endTime.length < 2) {
    return false;
  }

  return true;
};

export const validateSpecificDatesHoliday = (holiday: Holiday) => {
  // [시작일, 종료일]
  // 시작일/종료일 == [년, 월, 일]

  // 빈 배열인 경우
  if (holiday.specificDates.length < 1) {
    return false;
  }

  // 시작일이 유효하지 않은 경우
  if (holiday.specificDates[0].length != 3) {
    return false;
  }

  // 종료일이 있는 경우, 종료일 유효성 검사
  if (holiday.specificDates.length > 1 && holiday.specificDates[1].length != 3) {
    return false;
  }

  return true;
};

export const validateHoliday = (holiday: Holiday) => {
  switch (holiday.holidayType) {
    case 'SPECIFIC_DATE': // 2023-12-25
      return validateSpecificDatesHoliday(holiday);
    case 'WEEKLY_DAY': // 매주 월요일
      return holiday.daysOfWeek.length > 0;
    case 'MONTHLY_DATE': // 매월 1일
      return holiday.daysOfMonth.length > 0;
    case 'MONTHLY_WEEK_AND_DAY': // 매월 둘째주 화요일
      return holiday.weeksOfMonth.length > 0 && holiday.daysOfWeek.length > 0;
    default:
      return false;
  }
};

export const hasBusinessHour = (businessHour: BusinessHour) => {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ] as const;

  for (const day of days) {
    const allDay = businessHour[`${day}AllDay`];
    const startTime = businessHour[`${day}StartTime`];
    const endTime = businessHour[`${day}EndTime`];

    if (validateBusinessHour(allDay, startTime, endTime)) {
      return true;
    }
  }

  return false;
};

export const hasBreakTime = (breakTime: BreakTime) => {
  const days = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ] as const;

  for (const day of days) {
    const startTime = breakTime[`${day}StartTime`];
    const endTime = breakTime[`${day}EndTime`];

    if (validateBreakTime(startTime, endTime)) {
      return true;
    }
  }

  return false;
};

export const hasHoliday = (storeHolidayList: Holiday[]) => {
  for (const holiday of storeHolidayList) {
    if (validateHoliday(holiday)) {
      return true;
    }
  }

  return false;
};
